// src/screen/Finance/EventFinancialReport.tsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import apiClient from "../../services/apiClient";
import { useDragScroll } from "../../hooks/useDragScroll";
import { fetchTransactions } from "../../services/transactionApi";
import { getEventById, IEventItem } from "../../services/eventsApi";
import { getEventInventory } from "../../services/eventInventoryApi";
import {
  fetchInventoryPools,
  getInventoryByPool,
} from "../../services/inventoryPools";
import {
  PieChart as RPieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  BarChart as RBarChart,
  XAxis,
  YAxis,
  Tooltip,
  Bar,
  LabelList,
  CartesianGrid,
  Legend,
} from "recharts";
import { formatMoney } from "../../services/currency";

type BestSellerItem = {
  sku?: string;
  itemName: string;
  quantitySold: number;
  refundedQuantity?: number;
  totalRevenue: number;
  inventoryItemId?: string;
  category?: string;
  imageUrl?: string;
};

const normalizeBestSellers = (raw: any): BestSellerItem[] => {
  if (!Array.isArray(raw)) return [];

  return raw
    .map((item: any) => ({
      sku: item?.sku,
      itemName: item?.itemName ?? "",
      quantitySold: Math.max(0, Number(item?.quantitySold) || 0),
      refundedQuantity: Number(item?.refundedQuantity) || 0,
      totalRevenue: Number(item?.totalRevenue) || 0,
      inventoryItemId: item?.inventoryItemId,
      category: item?.category ?? "",
      imageUrl: item?.imageUrl,
    }))
    .filter((item) => item.quantitySold > 0)
    .sort((a, b) => {
      if (b.quantitySold !== a.quantitySold) {
        return b.quantitySold - a.quantitySold;
      }
      return b.totalRevenue - a.totalRevenue;
    });
};
type InventoryLite = {
  _id: string;
  itemName?: string;
  sku?: string;
  quantity?: number;
  imageUrl?: string;
};
type BestSellerInventorySummary = {
  poolQuantity: number;
  assignedQuantity: number;
};

type EventFinance = {
  eventId: string;
  eventTitle?: string;
  eventDescription?: string;
  eventLocation?: string;
  event?: {
    title?: string;
    description?: string;
    location?: string;
    startDateTime?: string;
    endDateTime?: string;
  };
  description?: string;
  location?: string;
  eventStartDateTime?: string;
  eventEndDateTime?: string;
  totalRevenue: number;
  totalCost: number;
  totalRefunds: number;
  totalDiscounts: number;
  netProfit: number;
  totalCommissionPaid?: number;
  totalCommissionForEmployee?: number;
  bestSellers: BestSellerItem[];
  paymentMethodBreakdown?: { paymentMethod: string; totalAmount: number }[];
  additionalCosts?: { description: string; amount: number }[];
};

type Tx = {
  _id?: string;
  items?: Array<{ itemName?: string }>;
  payments?: Array<{ method?: string }>;
  paymentMethod?: string;
  totalAmount?: number;
  saleDate?: string;
  createdAt?: string;
  userId?: { name?: string };
  madeBy?: string;
};

const COLORS = {
  green: "#22c55e",
  blue: "#3b82f6",
  red: "#ef4444",
  amber: "#f59e0b",
  violet: "#8b5cf6",
  slate: "#94a3b8",
};
const REPORT_BUILD_STAMP = "finance-fix-2026-07-07-2";
const REPORT_TIME_ZONE = "America/Chicago";
const currency = (n?: number) => formatMoney(n);
const summaryCurrency = (label: string, value?: number) =>
  label === "Net Profit" && Number(value || 0) < 0
    ? currency(Math.abs(Number(value || 0)))
    : currency(value);
const normalizeCategoryLabel = (value?: string) => {
  const label = (value || "").trim();
  return label || "Uncategorized";
};
const formatCount = (n: number) => Number(n || 0).toLocaleString();
const formatPercent = (n: number) => `${Number(n || 0).toFixed(1)}%`;
const formatCategoryMetric = (value: number, name: string) => {
  if (name === "Revenue %") return [formatPercent(Number(value)), name];
  return [formatCount(Number(value)), name];
};
const normalizeInventoryText = (value?: string) =>
  String(value || "")
    .trim()
    .toLowerCase();
const readInventoryId = (value: any) =>
  String(
    value?.inventoryItemId?._id ||
      value?.inventoryItem?._id ||
      value?.inventory?._id ||
      value?.inventoryId ||
      value?._id ||
      "",
  ).trim();
const matchesInventoryRecord = (
  value: any,
  bestSeller: { itemName?: string; sku?: string },
  inventoryId?: string,
) => {
  const candidateId = readInventoryId(value);
  if (inventoryId) return Boolean(candidateId && candidateId === inventoryId);

  const candidateSku = normalizeInventoryText(
    value?.inventoryItemId?.sku ||
      value?.inventoryItem?.sku ||
      value?.inventory?.sku ||
      value?.sku,
  );
  const bestSku = normalizeInventoryText(bestSeller.sku);
  if (bestSku) return Boolean(candidateSku && bestSku === candidateSku);

  const candidateName = normalizeInventoryText(
    value?.inventoryItemId?.itemName ||
      value?.inventoryItem?.itemName ||
      value?.inventory?.itemName ||
      value?.itemName,
  );
  const bestName = normalizeInventoryText(bestSeller.itemName);
  return Boolean(bestName && candidateName && bestName === candidateName);
};
const readAssignedQuantity = (value: any) => {
  const directCandidates = [
    value?.remainingQuantity,
    value?.quantityRemaining,
    value?.availableQuantity,
    value?.quantityLeft,
  ];
  for (const candidate of directCandidates) {
    const parsed = Number(candidate);
    if (Number.isFinite(parsed)) return Math.max(0, parsed);
  }

  const allocated = Number(value?.allocatedQuantity ?? value?.allocatedQty ?? 0);
  const sold = Number(value?.soldQuantity ?? value?.quantitySold ?? 0);
  if (Number.isFinite(allocated) || Number.isFinite(sold)) {
    return Math.max(0, allocated - sold);
  }
  return 0;
};

// Local-day helpers (match device/browser local calendar)
const ymdLocal = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dy = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dy}`;
};
const addDaysLocal = (d: Date, n: number) =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
const parseYMD_Local = (s: string) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return new Date(s);
  const y = Number(m[1]);
  const mo = Number(m[2]) - 1;
  const d = Number(m[3]);
  return new Date(y, mo, d);
};

// ---- Smart fetcher with debug of attempted URLs
type TrySpec = { method: "get" | "post"; url: string; body?: any };
async function tryGet(paths: TrySpec[]) {
  const tried: string[] = [];
  let lastErr: any = null;
  for (const p of paths) {
    try {
      tried.push(`${p.method.toUpperCase()} ${p.url}`);
      const res =
        p.method === "get"
          ? await apiClient.get(p.url)
          : await apiClient.post(p.url, p.body ?? {});
      if (res?.data) return { data: res.data, tried };
    } catch (e) {
      lastErr = e;
    }
  }
  if (lastErr) throw Object.assign(lastErr, { tried });
  throw Object.assign(new Error("No matching endpoint responded"), { tried });
}

function eventPaths(eventId: string): TrySpec[] {
  return [
    { method: "get", url: `/finance/${eventId}` },
  ];
}
function extractEventTitle(d?: any): string {
  if (!d) return "";
  return (
    d.eventTitle ||
    d.title ||
    d.name ||
    d.event?.title ||
    d.event?.name ||
    d.event_name ||
    d.event?.eventTitle ||
    ""
  );
}

function extractEventDescription(d?: any): string {
  if (!d) return "";
  return (
    d.eventDescription ||
    d.description ||
    d.event?.description ||
    d.event_description ||
    ""
  );
}

function extractEventLocation(d?: any): string {
  if (!d) return "";
  return (
    d.eventLocation ||
    d.location ||
    d.event?.location ||
    d.event_location ||
    ""
  );
}

function formatEventDate(value?: string) {
  if (!value) return "Not set";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not set";
  return date.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatEventTime(value?: string) {
  if (!value) return "Not set";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not set";
  return date.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
}

function rangePaths(eventId: string, s: string, e: string): TrySpec[] {
  const cacheBust = `_ts=${Date.now()}`;
  const q = `startDate=${encodeURIComponent(s)}&endDate=${encodeURIComponent(
    e,
  )}&timeZone=${encodeURIComponent(REPORT_TIME_ZONE)}&${cacheBust}`;
  return [
    { method: "get", url: `/finance/event/${eventId}/range?${q}` },
  ];
}
async function getEventFinanceSmart(eventId: string) {
  return await tryGet(eventPaths(eventId));
}
async function getEventFinanceByRangeSmart(
  eventId: string,
  s: string,
  e: string,
) {
  return await tryGet(rangePaths(eventId, s, e));
}

async function fetchInventoryLite(): Promise<InventoryLite[]> {
  const { data } = await apiClient.get("/inventory");
  const items = Array.isArray((data as any)?.items) ? (data as any).items : [];
  return items as InventoryLite[];
}

// -------- UI helpers
const truncate = (s: string, n = 28) =>
  s?.length > n ? s.slice(0, n - 1) + "…" : s;

const makeInventoryKey = (itemName?: string, sku?: string) => {
  const skuKey = (sku || "").trim().toLowerCase();
  if (skuKey) return `sku:${skuKey}`;
  return `name:${(itemName || "").trim().toLowerCase()}`;
};

const EventFinancialReport: React.FC = () => {
  type BestSortKey = "itemName" | "category" | "quantitySold" | "totalRevenue";
  type BestSortDir = "asc" | "desc";

  const { eventId = "" } = useParams();
  const [data, setData] = useState<EventFinance | null>(null);
  const [eventMeta, setEventMeta] = useState<IEventItem | null>(null);
  const [days, setDays] = useState<{ label: string; ymd: string }[]>([]);
  const [activeDay, setActiveDay] = useState<"ALL" | string>("ALL");
  const [loading, setLoading] = useState(false);
  const [errText, setErrText] = useState<string | null>(null);
  const [debugTried, setDebugTried] = useState<string[]>([]);
  const [transactions, setTransactions] = useState<Tx[]>([]);
  const [txLoading, setTxLoading] = useState(false);
  const [paymentFilter, setPaymentFilter] = useState<string>("All");
  const [txVisibleCount, setTxVisibleCount] = useState(10);
  const [selectedBestSeller, setSelectedBestSeller] =
    useState<BestSellerItem | null>(null);
  const [inventoryByKey, setInventoryByKey] = useState<
    Map<string, InventoryLite>
  >(new Map());
  const [inventoryById, setInventoryById] = useState<Map<string, InventoryLite>>(
    new Map(),
  );
  const [inventoryLoaded, setInventoryLoaded] = useState(false);
  const [bestSellerInventorySummary, setBestSellerInventorySummary] =
    useState<BestSellerInventorySummary>({
      poolQuantity: 0,
      assignedQuantity: 0,
    });
  const [bestSellerInventorySummaryCache, setBestSellerInventorySummaryCache] =
    useState<Map<string, BestSellerInventorySummary>>(new Map());
  const [bestSellerInventorySummaryLoading, setBestSellerInventorySummaryLoading] =
    useState(false);

  // Best sellers toggles + filters
  const [showAllBest, setShowAllBest] = useState(false);
  const [bestCategoryFilter, setBestCategoryFilter] = useState<string>("All");
  const [bestSearch, setBestSearch] = useState<string>("");
  const [bestSort, setBestSort] = useState<{
    key: BestSortKey;
    dir: BestSortDir;
  }>({ key: "totalRevenue", dir: "desc" });
  const financeRequestIdRef = useRef(0);

  const load = useCallback(async () => {
    const requestId = ++financeRequestIdRef.current;
    setLoading(true);
    setErrText(null);
    setDebugTried([]);
    try {
      const { data: d, tried } = await getEventFinanceSmart(eventId);
      if (requestId !== financeRequestIdRef.current) return;
      setData({ ...d, bestSellers: normalizeBestSellers(d?.bestSellers) });
      setDebugTried(tried || []);
      const financeLocation = extractEventLocation(d);

      if (!financeLocation) {
        try {
          const eventRecord = await getEventById(eventId);
          setEventMeta(eventRecord);
        } catch {
          setEventMeta(null);
        }
      } else {
        setEventMeta(null);
      }

      const sRaw = d.eventStartDateTime || d.event?.startDateTime;
      const eRaw = d.eventEndDateTime || d.event?.endDateTime || sRaw;

      if (sRaw) {
        const s = new Date(sRaw);
        const e = new Date(eRaw || sRaw);

        // clamp to LOCAL midnights so chips align with user's calendar
        const sLocal = new Date(s.getFullYear(), s.getMonth(), s.getDate());
        const eLocal = new Date(e.getFullYear(), e.getMonth(), e.getDate());

        const out: { label: string; ymd: string }[] = [];
        let cur = sLocal;
        while (cur.getTime() <= eLocal.getTime()) {
          out.push({
            ymd: ymdLocal(cur), // send this YYYY-MM-DD to the range API
            label: parseYMD_Local(ymdLocal(cur)).toLocaleDateString(undefined, {
              month: "short",
              day: "numeric",
            }),
          });
          cur = addDaysLocal(cur, 1);
        }
        setDays(out);
      } else {
        setDays([]);
      }

      setActiveDay("ALL");
    } catch (e: any) {
      if (requestId !== financeRequestIdRef.current) return;
      const status = e?.response?.status;
      const msg = e?.response?.data?.message || e?.message || "Failed to load";
      setErrText(`${status ? status + " • " : ""}${msg}`);
      setDebugTried(e?.tried || []);
    } finally {
      if (requestId !== financeRequestIdRef.current) return;
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!eventId) return;
      setTxLoading(true);
      try {
        const res = await fetchTransactions(eventId);
        const txs = Array.isArray(res?.data)
          ? res.data
          : res?.data?.transactions;
        if (mounted) setTransactions(Array.isArray(txs) ? txs : []);
      } catch {
        if (mounted) setTransactions([]);
      } finally {
        if (mounted) setTxLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [eventId]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const rows = await fetchInventoryLite();
        if (!mounted) return;
        const lookup = new Map<string, InventoryLite>();
        const idLookup = new Map<string, InventoryLite>();
        (rows || []).forEach((item) => {
          lookup.set(makeInventoryKey(item.itemName, item.sku), item);
          lookup.set(makeInventoryKey(item.itemName, ""), item);
          if (item._id) idLookup.set(String(item._id), item);
        });
        setInventoryByKey(lookup);
        setInventoryById(idLookup);
      } catch {
        if (mounted) {
          setInventoryByKey(new Map());
          setInventoryById(new Map());
        }
      } finally {
        if (mounted) setInventoryLoaded(true);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const onPickDay = useCallback(
    async (d: "ALL" | string) => {
      const requestId = ++financeRequestIdRef.current;
      setActiveDay(d);
      setLoading(true);
      setErrText(null);
      setDebugTried([]);
      try {
        const res =
          d === "ALL"
            ? await getEventFinanceSmart(eventId)
            : await getEventFinanceByRangeSmart(eventId, d, d);
        if (requestId !== financeRequestIdRef.current) return;
        setData({ ...res.data, bestSellers: normalizeBestSellers(res.data?.bestSellers) });
        setDebugTried(res.tried || []);
      } catch (e: any) {
        if (requestId !== financeRequestIdRef.current) return;
        const status = e?.response?.status;
        const msg =
          e?.response?.data?.message || e?.message || "Failed to filter";
        setErrText(`${status ? status + " • " : ""}${msg}`);
        setDebugTried(e?.tried || []);
      } finally {
        if (requestId !== financeRequestIdRef.current) return;
        setLoading(false);
      }
    },
    [eventId],
  );

  useEffect(() => {
    setTxVisibleCount(10);
    setPaymentFilter("All");
  }, [activeDay, transactions]);

  const summaryCards = useMemo(() => {
    const d = data || ({} as EventFinance);
    const base = [
      { label: "Total Revenue", value: d.totalRevenue, color: COLORS.blue },
      {
        label: "Net Profit",
        value: d.netProfit,
        color: Number(d.netProfit || 0) < 0 ? COLORS.red : COLORS.green,
      },
      { label: "Total Cost", value: d.totalCost, color: COLORS.red },
      {
        label: "Employee Payouts",
        value: d.totalCommissionPaid || 0,
        color: COLORS.violet,
      },
      { label: "Discounts", value: d.totalDiscounts, color: COLORS.amber },
      { label: "Refunds", value: d.totalRefunds, color: COLORS.red },
    ];
    const pm =
      (d.paymentMethodBreakdown || []).map((p) => ({
        label: p.paymentMethod,
        value: p.totalAmount,
        color: COLORS.slate,
      })) || [];
    return [...base, ...pm];
  }, [data]);

  const pieSlices = useMemo(() => {
    const d = data || ({} as EventFinance);
    return [
      { name: "Revenue", value: d.totalRevenue || 0, color: COLORS.green },
      { name: "Refunds", value: d.totalRefunds || 0, color: COLORS.red },
      {
        name: "Payouts",
        value: d.totalCommissionPaid || 0,
        color: COLORS.violet,
      },
      { name: "Discounts", value: d.totalDiscounts || 0, color: COLORS.amber },
      { name: "Net Profit", value: d.netProfit || 0, color: COLORS.blue },
    ];
  }, [data]);

  const categoryAnalysis = useMemo(() => {
    const acc = new Map<
      string,
      { category: string; totalRevenue: number; totalQuantity: number }
    >();
    (data?.bestSellers || []).forEach((s) => {
      const category = normalizeCategoryLabel(s.category);
      const key = category.toLowerCase();
      const prev = acc.get(key) || {
        category,
        totalRevenue: 0,
        totalQuantity: 0,
      };
      prev.totalRevenue += Number(s.totalRevenue || 0);
      prev.totalQuantity += Number(s.quantitySold || 0);
      acc.set(key, prev);
    });
    return Array.from(acc.values()).sort((a, b) => {
      const revenueDiff = b.totalRevenue - a.totalRevenue;
      if (revenueDiff !== 0) return revenueDiff;
      return b.totalQuantity - a.totalQuantity;
    });
  }, [data?.bestSellers]);
  const categoryAnalysisWithPct = useMemo(() => {
    const totalRevenue = categoryAnalysis.reduce(
      (sum, row) => sum + Number(row.totalRevenue || 0),
      0,
    );
    return categoryAnalysis.map((row) => ({
      ...row,
      revenueSharePct:
        totalRevenue > 0
          ? (Number(row.totalRevenue || 0) / totalRevenue) * 100
          : 0,
    }));
  }, [categoryAnalysis]);
  const categoryAnalysisWithPctNumbered = useMemo(
    () =>
      categoryAnalysisWithPct.map((row, index) => ({
        ...row,
        categoryLabel: `${index + 1}. ${row.category}`,
      })),
    [categoryAnalysisWithPct],
  );
  const categoryChartMinWidth = useMemo(
    () => Math.max(540, categoryAnalysisWithPct.length * 128),
    [categoryAnalysisWithPct.length],
  );
  const {
    ref: categoryChartScrollRef,
    isDragging: isCategoryChartDragging,
    dragHandlers: categoryChartDragHandlers,
  } = useDragScroll<HTMLDivElement>();

  /* ---------- Best Sellers: filters + search (like FinanceDashboard) ---------- */
  const bestSellerCategories = useMemo(() => {
    const set = new Set<string>();
    (data?.bestSellers || []).forEach((b) => {
      const raw = (b.category || "Uncategorized").trim();
      set.add(raw || "Uncategorized");
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [data?.bestSellers]);

  const bestSellersFiltered = useMemo(() => {
    const full = data?.bestSellers || [];
    let out = full;

    if (bestCategoryFilter !== "All") {
      const target = bestCategoryFilter.trim().toLowerCase();
      out = out.filter((b) => {
        const cat = (b.category || "Uncategorized").trim() || "Uncategorized";
        return cat.toLowerCase() === target;
      });
    }

    if (bestSearch.trim()) {
      const q = bestSearch.trim().toLowerCase();
      out = out.filter((b) => {
        const name = (b.itemName || "").toLowerCase();
        const sku = (b.sku || "").toLowerCase();
        return name.includes(q) || sku.includes(q);
      });
    }

    return out;
  }, [data?.bestSellers, bestCategoryFilter, bestSearch]);

  const onBestHeaderClick = useCallback((key: BestSortKey) => {
    setShowAllBest(false);
    setBestSort((prev) => {
      if (prev.key === key) {
        return { key, dir: prev.dir === "asc" ? "desc" : "asc" };
      }
      const defaultDir: BestSortDir =
        key === "itemName" || key === "category" ? "asc" : "desc";
      return { key, dir: defaultDir };
    });
  }, []);

  // Visible best sellers: sorted by selected column then show top 5 unless expanded
  const visibleBest = useMemo(() => {
    const list = [...bestSellersFiltered].sort((a, b) => {
      const direction = bestSort.dir === "asc" ? 1 : -1;
      let cmp = 0;

      if (bestSort.key === "itemName") {
        cmp = (a.itemName || "").localeCompare(b.itemName || "", undefined, {
          sensitivity: "base",
        });
      } else if (bestSort.key === "category") {
        const aCat = (a.category || "Uncategorized").trim() || "Uncategorized";
        const bCat = (b.category || "Uncategorized").trim() || "Uncategorized";
        cmp = aCat.localeCompare(bCat, undefined, { sensitivity: "base" });
      } else if (bestSort.key === "quantitySold") {
        cmp = Number(a.quantitySold || 0) - Number(b.quantitySold || 0);
      } else {
        cmp = Number(a.totalRevenue || 0) - Number(b.totalRevenue || 0);
      }

      if (cmp !== 0) return cmp * direction;

      const revenueDiff =
        Number(b.totalRevenue || 0) - Number(a.totalRevenue || 0);
      if (revenueDiff !== 0) return revenueDiff;
      const qtyDiff = Number(b.quantitySold || 0) - Number(a.quantitySold || 0);
      if (qtyDiff !== 0) return qtyDiff;

      return (a.itemName || "").localeCompare(b.itemName || "", undefined, {
        sensitivity: "base",
      });
    });
    return showAllBest ? list : list.slice(0, 5);
  }, [bestSellersFiltered, showAllBest, bestSort]);
  const selectedBestSellerInventory = useMemo(() => {
    if (!selectedBestSeller) return null;
    if (selectedBestSeller.inventoryItemId) {
      const byId = inventoryById.get(String(selectedBestSeller.inventoryItemId));
      if (byId) return byId;
    }
    const bySku = inventoryByKey.get(
      makeInventoryKey(selectedBestSeller.itemName, selectedBestSeller.sku)
    );
    if (bySku) return bySku;
    return inventoryByKey.get(makeInventoryKey(selectedBestSeller.itemName, ""));
  }, [selectedBestSeller, inventoryById, inventoryByKey]);
  const selectedBestSellerSummaryKey = useMemo(() => {
    if (!selectedBestSeller) return "";
    return selectedBestSeller.inventoryItemId
      ? `inventory:${selectedBestSeller.inventoryItemId}`
      : makeInventoryKey(
          selectedBestSeller.itemName,
          selectedBestSellerInventory?._id || selectedBestSeller.sku || "",
        );
  }, [selectedBestSeller, selectedBestSellerInventory]);

  useEffect(() => {
    let active = true;

    if (!selectedBestSeller) {
      setBestSellerInventorySummary({ poolQuantity: 0, assignedQuantity: 0 });
      setBestSellerInventorySummaryLoading(false);
      return;
    }

    if (!inventoryLoaded) {
      setBestSellerInventorySummaryLoading(true);
      return () => {
        active = false;
      };
    }

    const cached = bestSellerInventorySummaryCache.get(selectedBestSellerSummaryKey);
    if (cached) {
      setBestSellerInventorySummary(cached);
      setBestSellerInventorySummaryLoading(false);
      return () => {
        active = false;
      };
    }

    (async () => {
      setBestSellerInventorySummaryLoading(true);
      try {
        const inventoryId = selectedBestSellerInventory?._id;
        const [pools, eventInventoryRows] = await Promise.all([
          fetchInventoryPools().catch(() => []),
          eventId ? getEventInventory(eventId).catch(() => []) : Promise.resolve([]),
        ]);

        const poolQuantity = (
          await Promise.all(
            (pools || [])
              .filter((pool) => !pool.isDefault && String(pool._id) !== "main")
              .map(async (pool) => {
                const items = await getInventoryByPool(String(pool._id)).catch(
                  () => [],
                );
                return (items || [])
                  .filter((item) =>
                    matchesInventoryRecord(item, selectedBestSeller, inventoryId),
                  )
                  .reduce((sum, item) => sum + Number(item.quantity || 0), 0);
              }),
          )
        ).reduce((sum, qty) => sum + Number(qty || 0), 0);

        const assignedQuantity = (Array.isArray(eventInventoryRows)
          ? eventInventoryRows
          : []
        )
          .filter((row) =>
            matchesInventoryRecord(row, selectedBestSeller, inventoryId),
          )
          .reduce((sum, row) => sum + readAssignedQuantity(row), 0);

        if (!active) return;
        const summary = {
          poolQuantity,
          assignedQuantity,
        };
        setBestSellerInventorySummary(summary);
        setBestSellerInventorySummaryCache((prev) => {
          const next = new Map(prev);
          next.set(selectedBestSellerSummaryKey, summary);
          return next;
        });
      } finally {
        if (active) setBestSellerInventorySummaryLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [
    eventId,
    inventoryLoaded,
    bestSellerInventorySummaryCache,
    selectedBestSeller,
    selectedBestSellerInventory,
    selectedBestSellerSummaryKey,
  ]);


  const title = extractEventTitle(data) || `Event ${eventId}`;
  const eventDescription = extractEventDescription(data);
  const eventLocation = extractEventLocation(data) || eventMeta?.location || "";
  const eventStart =
    data?.event?.startDateTime || data?.eventStartDateTime || eventMeta?.startDateTime;
  const eventEnd =
    data?.event?.endDateTime ||
    data?.eventEndDateTime ||
    eventMeta?.endDateTime ||
    eventStart;

  const getTxLocalYmd = (tx: Tx) => {
    const raw = tx.saleDate || tx.createdAt;
    if (!raw) return "";
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return "";
    return ymdLocal(d);
  };

  const paymentMethods = useMemo(() => {
    const set = new Set<string>();
    (transactions || []).forEach((tx) => {
      if (Array.isArray(tx.payments) && tx.payments.length) {
        tx.payments.forEach((p) => p?.method && set.add(String(p.method)));
      } else if (tx.paymentMethod) {
        set.add(String(tx.paymentMethod));
      }
    });
    return ["All", ...Array.from(set)];
  }, [transactions]);

  const filteredTransactions = useMemo(() => {
    if (!transactions || !transactions.length) return [];
    let list = transactions;
    if (activeDay !== "ALL") {
      list = list.filter((tx) => getTxLocalYmd(tx) === activeDay);
    }
    if (paymentFilter === "All") return list;
    return list.filter((tx) => {
      if (Array.isArray(tx.payments) && tx.payments.length) {
        return tx.payments.some((p) => p?.method === paymentFilter);
      }
      return tx.paymentMethod === paymentFilter;
    });
  }, [transactions, activeDay, paymentFilter]);

  return (
    <div className="px-4 md:px-6 py-4 mt-5 ">
      <div className="mx-auto max-w-6xl space-y-4 ">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 ">
          <div className="min-w-0 ">
            <h1 className="text-xl font-semibold leading-tight truncate">
              {title}
            </h1>
            <p className="text-xs text-slate-500">
              {(() => {
                const s =
                  data?.event?.startDateTime || data?.eventStartDateTime;
                const e =
                  data?.event?.endDateTime || data?.eventEndDateTime || s;
                if (!s) return "";
                const sTxt = new Date(s).toLocaleDateString(undefined, {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                });
                const eTxt = e
                  ? new Date(e).toLocaleDateString(undefined, {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })
                  : "";
                return eTxt && eTxt !== sTxt ? `${sTxt} – ${eTxt}` : sTxt;
              })()}
            </p>
            <p className="text-[10px] text-slate-400 mt-1">
              Build {REPORT_BUILD_STAMP} • Active {activeDay} • TZ {REPORT_TIME_ZONE}
            </p>
          </div>
          <Link
            to="/finance"
            className="text-sm text-blue-600 hover:underline shrink-0"
          >
            ← Back to Finance
          </Link>
        </div>

        {/* Error */}
        {errText && (
          <div className="p-3 rounded-md border border-red-200 bg-red-50 text-red-700">
            <div className="font-medium text-sm">{errText}</div>
            {debugTried.length > 0 && (
              <details className="mt-1">
                <summary className="cursor-pointer text-xs">
                  Endpoints tried
                </summary>
                <ul className="list-disc ml-5 text-[11px] mt-1 space-y-0.5">
                  {debugTried.map((t, i) => (
                    <li key={i} className="break-all">
                      {t}
                    </li>
                  ))}
                </ul>
              </details>
            )}
            <div className="mt-2">
              <button
                onClick={load}
                className="text-sm text-blue-600 underline"
              >
                Retry
              </button>
            </div>
          </div>
        )}

        {/* Date chips (compact) */}
        {days.length > 1 && (
          <div className="flex flex-wrap items-center gap-1.5 ">
            <button
              onClick={() => onPickDay("ALL")}
              className={`px-2.5 py-1 rounded-md border text-xs bg-white ${
                activeDay === "ALL"
                  ? "bg-indigo-600 text-black border-indigo-600"
                  : "border-slate-300 hover:bg-slate-50"
              }`}
            >
              All Days
            </button>
            {days.map((d) => {
              const selected = activeDay === d.ymd;
              return (
                <button
                  key={d.ymd}
                  onClick={() => onPickDay(d.ymd)}
                  className={`px-2.5 py-1 rounded-md border text-xs bg-white ${
                    selected
                      ? "bg-indigo-600 text-black border-indigo-600"
                      : "border-slate-300 hover:bg-slate-50"
                  }`}
                >
                  {d.label}
                </button>
              );
            })}
          </div>
        )}

        <div className="rounded-md border bg-white">
          <div className="px-3 py-2 border-b">
            <div className="font-medium text-sm">Event Info</div>
          </div>
          <div className="grid grid-cols-1 gap-3 p-3 md:grid-cols-3">
            <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
              <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
                Location
              </div>
              <div className="mt-1 text-sm font-medium text-slate-900">
                {eventLocation || "No location added"}
              </div>
            </div>
            <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
              <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
                Date
              </div>
              <div className="mt-1 text-sm font-medium text-slate-900">
                {eventStart && eventEnd && formatEventDate(eventStart) !== formatEventDate(eventEnd)
                  ? `${formatEventDate(eventStart)} - ${formatEventDate(eventEnd)}`
                  : formatEventDate(eventStart)}
              </div>
            </div>
            <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
              <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
                Time
              </div>
              <div className="mt-1 text-sm font-medium text-slate-900">
                {eventStart
                  ? `${formatEventTime(eventStart)}${eventEnd ? ` - ${formatEventTime(eventEnd)}` : ""}`
                  : "Not set"}
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        {!data && !loading ? null : (
          <>
            {/* Summary cards (compact) */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
              {summaryCards.map((c, i) => (
                <div key={i} className="rounded-md border p-3 bg-white">
                  <div className="text-[11px] text-slate-500 leading-tight">
                    {c.label}
                  </div>
                  <div
                    className="text-base font-semibold tabular-nums"
                    style={{ color: c.color }}
                  >
                    {summaryCurrency(c.label, c.value)}
                  </div>
                </div>
              ))}
            </div>

            {/* Charts (slightly shorter) */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-3 ">
              <div className="rounded-md border p-3 bg-white">
                <div className="mb-1 font-medium text-sm">Percentage Pie</div>
                <div className="h-56">
                  <ResponsiveContainer>
                    <RPieChart>
                      <Pie
                        dataKey="value"
                        data={pieSlices}
                        nameKey="name"
                        innerRadius={54}
                        outerRadius={92}
                      >
                        {pieSlices.map((s, i) => (
                          <Cell key={i} fill={s.color} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v: any) => currency(Number(v))} />
                      <Legend />
                    </RPieChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="rounded-md border p-3 bg-white">
                <div className="mb-1 flex items-center gap-2 font-medium text-sm ">
                  <span>Category Analysis</span>
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600">
                    {categoryAnalysis.length}
                  </span>
                </div>
                <div className="space-y-2">
                  <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
                    <div className="flex items-center gap-3">
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-violet-50 px-2 py-1 text-violet-700">
                        <span className="h-2 w-2 rounded-full bg-violet-600" />
                        Qty
                      </span>
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-2 py-1 text-blue-700">
                        <span className="h-2 w-2 rounded-full bg-blue-600" />
                        Revenue %
                      </span>
                    </div>
                    <span className="text-slate-500">
                      Click and drag to scroll
                    </span>
                  </div>
                  <div
                    ref={categoryChartScrollRef}
                    {...categoryChartDragHandlers}
                    className={`h-72 overflow-x-auto overflow-y-hidden rounded-xl border border-slate-200 bg-gradient-to-b from-white to-slate-50/70 pl-1 pr-1 pt-3 pb-1 select-none [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden [&_.recharts-wrapper]:cursor-inherit [&_.recharts-surface]:cursor-inherit [&_.recharts-layer]:cursor-inherit [&_.recharts-cartesian-axis-tick-value]:cursor-inherit ${
                      isCategoryChartDragging
                        ? "cursor-grabbing"
                        : "cursor-grab"
                    }`}
                  >
                    <div
                      className="h-full"
                      style={{ minWidth: `${categoryChartMinWidth}px` }}
                    >
                      <ResponsiveContainer>
                        <RBarChart
                          data={categoryAnalysisWithPctNumbered}
                          barGap={12}
                          barCategoryGap="34%"
                          margin={{ top: 34, right: 14, left: -8, bottom: 26 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis
                            dataKey="categoryLabel"
                            interval={0}
                            height={46}
                            angle={-14}
                            textAnchor="end"
                            tickMargin={8}
                            tickFormatter={(t) => String(t)}
                          />
                          <YAxis
                            yAxisId="qty"
                            allowDecimals={false}
                            tickFormatter={(v: any) => formatCount(Number(v))}
                          />
                          <YAxis
                            yAxisId="revenue"
                            orientation="right"
                            domain={[0, 100]}
                            tickFormatter={(v: any) => formatPercent(Number(v))}
                          />
                          <Tooltip
                            formatter={(v: any, name: any) =>
                              formatCategoryMetric(Number(v), String(name))
                            }
                          />
                          <Bar
                            yAxisId="qty"
                            dataKey="totalQuantity"
                            name="Qty"
                            fill={COLORS.violet}
                            barSize={18}
                          >
                            <LabelList
                              dataKey="totalQuantity"
                              position="top"
                              formatter={(v: any) => formatCount(Number(v))}
                              style={{
                                fill: "#5b21b6",
                                fontSize: 11,
                                fontWeight: 600,
                              }}
                            />
                          </Bar>
                          <Bar
                            yAxisId="revenue"
                            dataKey="revenueSharePct"
                            name="Revenue %"
                            fill={COLORS.blue}
                            barSize={18}
                          >
                            <LabelList
                              dataKey="revenueSharePct"
                              position="top"
                              formatter={(v: any) => formatPercent(Number(v))}
                              style={{
                                fill: "#1d4ed8",
                                fontSize: 11,
                                fontWeight: 600,
                              }}
                            />
                          </Bar>
                        </RBarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Best Sellers (filters + search + table) */}
            <div className="rounded-md border bg-white">
              <div className="px-3 pt-2 pb-2 border-b space-y-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <div className="font-medium text-sm">Best Sellers</div>
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600">
                      {bestSellersFiltered.length}
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="search"
                      value={bestSearch}
                      onChange={(e) => {
                        setBestSearch(e.target.value);
                        setShowAllBest(false);
                      }}
                      placeholder="Search item or SKU"
                      className="border border-slate-300 rounded-md px-2 py-1 text-xs w-40 md:w-56"
                    />
                  </div>
                </div>

                {/* Category filter chips */}
                <div className="flex flex-wrap gap-2">
                  <button
                    className={`px-2.5 py-1 rounded-full text-xs border ${
                      bestCategoryFilter === "All"
                        ? "bg-blue-600 text-white border-blue-600"
                        : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
                    }`}
                    onClick={() => {
                      setBestCategoryFilter("All");
                      setShowAllBest(false);
                    }}
                  >
                    All
                  </button>

                  {bestSellerCategories.map((cat) => {
                    const active = bestCategoryFilter === cat;
                    return (
                      <button
                        key={cat}
                        onClick={() => {
                          setBestCategoryFilter(active ? "All" : cat);
                          setShowAllBest(false);
                        }}
                        className={`px-2.5 py-1 rounded-full text-xs border ${
                          active
                            ? "bg-blue-600 text-white border-blue-600"
                            : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
                        }`}
                        title={cat}
                      >
                        {cat}
                      </button>
                    );
                  })}
                </div>
              </div>

              {bestSellersFiltered.length === 0 ? (
                <div className="px-3 py-3 text-sm text-slate-500">
                  {(data?.bestSellers?.length || 0) === 0
                    ? "No best sellers found."
                    : "No best sellers match your filters."}
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead className="bg-slate-50 text-slate-600">
                        <tr>
                          <th className="w-12 px-3 py-2 text-right font-medium">
                            #
                          </th>
                          <th className="px-3 py-2 text-left font-medium">
                            <button
                              type="button"
                              className="inline-flex items-center gap-1.5 hover:text-slate-900"
                              onClick={() => onBestHeaderClick("itemName")}
                              title="Sort by item"
                            >
                              <span className="text-[15px] font-black leading-none text-slate-800">
                                {bestSort.key === "itemName"
                                  ? bestSort.dir === "asc"
                                    ? "↑"
                                    : "↓"
                                  : "↕"}
                              </span>
                              Item
                            </button>
                          </th>
                          <th className="px-3 py-2 text-left font-medium">
                            <button
                              type="button"
                              className="inline-flex items-center gap-1.5 hover:text-slate-900"
                              onClick={() => onBestHeaderClick("category")}
                              title="Sort by category"
                            >
                              <span className="text-[15px] font-black leading-none text-slate-800">
                                {bestSort.key === "category"
                                  ? bestSort.dir === "asc"
                                    ? "↑"
                                    : "↓"
                                  : "↕"}
                              </span>
                              Category
                            </button>
                          </th>
                          <th className="px-3 py-2 text-right font-medium">
                            <button
                              type="button"
                              className="inline-flex items-center gap-1.5 hover:text-slate-900"
                              onClick={() => onBestHeaderClick("quantitySold")}
                              title="Sort by quantity"
                            >
                              <span className="text-[15px] font-black leading-none text-slate-800">
                                {bestSort.key === "quantitySold"
                                  ? bestSort.dir === "asc"
                                    ? "↑"
                                    : "↓"
                                  : "↕"}
                              </span>
                              Qty
                            </button>
                          </th>
                          <th className="px-3 py-2 text-right font-medium">
                            <button
                              type="button"
                              className="inline-flex items-center gap-1.5 hover:text-slate-900"
                              onClick={() => onBestHeaderClick("totalRevenue")}
                              title="Sort by revenue"
                            >
                              <span className="text-[15px] font-black leading-none text-slate-800">
                                {bestSort.key === "totalRevenue"
                                  ? bestSort.dir === "asc"
                                    ? "↑"
                                    : "↓"
                                  : "↕"}
                              </span>
                              Revenue
                            </button>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {visibleBest.map((b, i) => (
                          <tr
                            key={`${b.sku || b.itemName}-${i}`}
                            className="cursor-pointer even:bg-slate-50/40 hover:bg-blue-50/50"
                            onClick={() => setSelectedBestSeller(b)}
                            title="View item details"
                          >
                            <td className="px-3 py-2 text-right text-slate-500 tabular-nums">
                              {i + 1}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              <div className="font-medium leading-tight">
                                {truncate(b.itemName, 40)}
                              </div>
                              {b.sku ? (
                                <div className="text-[11px] text-slate-500">
                                  SKU: {b.sku}
                                </div>
                              ) : null}
                            </td>
                            <td className="px-3 py-2 text-slate-600">
                              {b.category || "Uncategorized"}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums">
                              {b.quantitySold}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums">
                              {currency(b.totalRevenue)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {bestSellersFiltered.length > 5 && (
                    <div className="px-3 py-2 border-t flex justify-end">
                      <button
                        className="text-sm text-blue-600"
                        onClick={() => setShowAllBest((v) => !v)}
                      >
                        {showAllBest ||
                        visibleBest.length >= bestSellersFiltered.length
                          ? "Show less"
                          : "Show more"}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>

            {selectedBestSeller && (
              <div
                className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
                onClick={() => setSelectedBestSeller(null)}
              >
                <div
                  className="w-full max-w-xl rounded-xl border border-slate-200 bg-white p-4 shadow-xl"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="mb-3 flex items-start justify-between gap-3">
                    <div>
                      <div className="text-base font-semibold text-slate-900">
                        {selectedBestSeller.itemName}
                      </div>
                      <div className="text-xs text-slate-500">
                        {selectedBestSeller.sku
                          ? `SKU: ${selectedBestSeller.sku}`
                          : "No SKU"}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setSelectedBestSeller(null)}
                      className="rounded-md border border-slate-300 px-2.5 py-1 text-xs text-slate-700 hover:bg-slate-50"
                    >
                      Close
                    </button>
                  </div>

                  <div className="h-80 w-full overflow-hidden rounded-lg border border-slate-200 bg-slate-900/90">
                    {selectedBestSellerInventory?.imageUrl ||
                    selectedBestSeller.imageUrl ? (
                      <img
                        src={
                          selectedBestSellerInventory?.imageUrl ||
                          selectedBestSeller.imageUrl
                        }
                        alt={selectedBestSeller.itemName}
                        className="h-full w-full object-contain"
                      />
                    ) : (
                      <div className="flex h-full items-center justify-center text-sm text-slate-500">
                        No Image
                      </div>
                    )}
                  </div>

                  <div className="mt-4 grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
                    <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
                      <div className="text-xs text-slate-500">
                        Quantity Left (Main Inventory)
                      </div>
                      <div className="text-lg font-semibold tabular-nums text-slate-900">
                        {selectedBestSellerInventory?.quantity ?? "—"}
                      </div>
                    </div>
                    <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
                      <div className="text-xs text-slate-500">
                        Quantity Left (Other Pools)
                      </div>
                      <div className="text-lg font-semibold tabular-nums text-slate-900">
                        {bestSellerInventorySummaryLoading
                          ? "Loading..."
                          : bestSellerInventorySummary.poolQuantity}
                      </div>
                    </div>
                    <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
                      <div className="text-xs text-slate-500">
                        Assigned to This Event
                      </div>
                      <div className="text-lg font-semibold tabular-nums text-slate-900">
                        {bestSellerInventorySummaryLoading
                          ? "Loading..."
                          : bestSellerInventorySummary.assignedQuantity}
                      </div>
                    </div>
                    <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
                      <div className="text-xs text-slate-500">Quantity Sold</div>
                      <div className="text-lg font-semibold tabular-nums text-slate-900">
                        {selectedBestSeller.quantitySold}
                      </div>
                    </div>
                    <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
                      <div className="text-xs text-slate-500">Category</div>
                      <div className="font-medium text-slate-900">
                        {selectedBestSeller.category || "Uncategorized"}
                      </div>
                    </div>
                    <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
                      <div className="text-xs text-slate-500">Revenue</div>
                      <div className="font-medium tabular-nums text-slate-900">
                        {currency(selectedBestSeller.totalRevenue)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Additional Costs (table, tight) */}
            {eventDescription ? (
              <div className="rounded-md border bg-white">
                <div className="px-3 py-2 border-b">
                  <div className="font-medium text-sm">Description</div>
                </div>
                <div className="px-3 py-3">
                  <div className="text-sm text-slate-700 whitespace-pre-wrap">
                    {eventDescription}
                  </div>
                </div>
              </div>
            ) : null}

            <div className="rounded-md border bg-white">
              <div className="px-3 py-2 border-b">
                <div className="font-medium text-sm">Additional Costs</div>
              </div>
              {(data?.additionalCosts || []).length === 0 ? (
                <div className="px-3 py-3 text-sm text-slate-500">
                  No additional costs recorded.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-slate-50 text-slate-600">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium">
                          Description
                        </th>
                        <th className="px-3 py-2 text-right font-medium">
                          Amount
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data?.additionalCosts || []).map((c, i) => (
                        <tr key={`cost-${i}`} className="even:bg-slate-50/40">
                          <td className="px-3 py-2">
                            {truncate(c.description, 64)}
                          </td>
                          <td className="px-3 py-2 text-right text-red-600 tabular-nums">
                            {currency(c.amount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Transactions (filters + table) */}
            <div className="rounded-md border bg-white">
              <div className="px-3 py-2 border-b flex items-center justify-between gap-2">
                <div className="font-medium text-sm">Transactions</div>
                {paymentMethods.length > 1 && (
                  <div className="flex flex-wrap gap-2">
                    {paymentMethods.map((method) => {
                      const selected = paymentFilter === method;
                      return (
                        <button
                          key={method}
                          onClick={() => setPaymentFilter(method)}
                          className={`px-2.5 py-1 rounded-full text-xs border ${
                            selected
                              ? "bg-blue-600 text-white border-blue-600"
                              : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
                          }`}
                        >
                          {method}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {txLoading ? (
                <div className="px-3 py-3 text-sm text-slate-500">
                  Loading transactions…
                </div>
              ) : filteredTransactions.length === 0 ? (
                <div className="px-3 py-3 text-sm text-slate-500">
                  No transactions recorded.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-slate-50 text-slate-600">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium">
                          Items
                        </th>
                        <th className="px-3 py-2 text-left font-medium">
                          Made By
                        </th>
                        <th className="px-3 py-2 text-right font-medium">
                          Amount
                        </th>
                        <th className="px-3 py-2 text-right font-medium">
                          Date
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredTransactions
                        .slice(0, txVisibleCount)
                        .map((tx, i) => {
                          const itemNames =
                            tx.items
                              ?.map((it) => it?.itemName)
                              .filter(Boolean) || [];
                          const joined = itemNames.join(", ");
                          const displayItems =
                            joined.length > 50
                              ? `${joined.slice(0, 50)}…`
                              : joined || "No items";
                          const amount = Number(tx.totalAmount || 0);
                          const dateRaw = tx.saleDate || tx.createdAt;
                          const dateText = dateRaw
                            ? new Date(dateRaw).toLocaleString()
                            : "";
                          return (
                            <tr
                              key={tx._id || `tx-${i}`}
                              className="even:bg-slate-50/40"
                            >
                              <td className="px-3 py-2">
                                <div className="font-medium">
                                  {displayItems}
                                </div>
                              </td>
                              <td className="px-3 py-2 text-slate-600">
                                {tx.userId?.name || tx.madeBy || "Unknown"}
                              </td>
                              <td className="px-3 py-2 text-right tabular-nums">
                                {currency(amount)}
                              </td>
                              <td className="px-3 py-2 text-right text-slate-500">
                                {dateText}
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              )}
              {filteredTransactions.length > txVisibleCount && (
                <div className="px-3 py-3">
                  <button
                    onClick={() => setTxVisibleCount((v) => v + 10)}
                    className="text-sm text-blue-600 hover:underline"
                  >
                    Show more
                  </button>
                </div>
              )}
            </div>
          </>
        )}

        {loading && <div className="p-6 text-slate-600">Loading…</div>}
      </div>
    </div>
  );
};

export default EventFinancialReport;
